import React, { useState } from 'react';
import { 
    FormControl, 
    InputLabel, 
    MenuItem, 
    Select, 
    TextField, 
    Grid, 
    SelectChangeEvent 
} from '@mui/material';
import { Translate } from 'react-localize-redux';
import { ButtonCancel, ButtonOk } from '../../../components/general/mini_components';
import { PaymentMethod, CreatePaymentRequest } from './types';
import ChipPaymentMethod from './ChipPaymentMethod';

interface PaymentMethodModalProps {
    paymentMethods: PaymentMethod[];
    onClose: () => void;
    onSelectPaymentMethod: (paymentRecord: CreatePaymentRequest) => void;
    activeLanguage: { code: string };
}

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
    paymentMethods,
    onClose,
    onSelectPaymentMethod,
    activeLanguage
}) => {
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
    const [bankName, setBankName] = useState('');
    const [chequeNumber, setChequeNumber] = useState('');
    const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
    const [paymentAmountDisplay, setPaymentAmountDisplay] = useState<string>('');

    // Set default to CASH if available
    React.useEffect(() => {
        if (!selectedPaymentMethod && paymentMethods && paymentMethods.length > 0) {
            const cashMethod = paymentMethods.find(pm => pm.code === 'CASH');
            if (cashMethod) {
                setSelectedPaymentMethod(cashMethod);
            }
        }
    }, [paymentMethods, selectedPaymentMethod]);

    const handlePaymentMethodChange = (event: SelectChangeEvent<number>) => {
        const selectedId = event.target.value as number;
        const selected = paymentMethods.find(pm => pm.id === selectedId) || null;
        setSelectedPaymentMethod(selected);
        
        // Reset cheque fields when changing payment method
        setBankName('');
        setChequeNumber('');
    };

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setPaymentAmountDisplay(inputValue);

        if (inputValue.trim() === '') {
            setPaymentAmount(null);
            return;
        }

        // Parse the number considering locale
        const parsedValue = parseFloat(inputValue.replace(/[^\d.,]/g, '').replace(',', '.'));
        if (!isNaN(parsedValue)) {
            setPaymentAmount(parsedValue);
        } else {
            setPaymentAmount(null);
        }
    };

    const handleAmountBlur = () => {
        // Format the display value when input loses focus
        if (paymentAmount !== null && paymentAmount >= 0) {
            const formatted = new Intl.NumberFormat(activeLanguage.code, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(paymentAmount);
            setPaymentAmountDisplay(formatted);
        }
    };

    const handleAmountFocus = () => {
        // Show raw number when input gains focus for easier editing
        if (paymentAmount !== null && paymentAmount >= 0) {
            setPaymentAmountDisplay(paymentAmount.toString());
        } else {
            setPaymentAmountDisplay('');
        }
    };

    const handleConfirm = () => {
        if (!selectedPaymentMethod || paymentAmount === null || paymentAmount < 0) return;

        const paymentRecord = {
            amount: paymentAmount,
            paymentMethodInvestigationId: selectedPaymentMethod.id,
            paymentData: selectedPaymentMethod.code === 'CHEQUE' ? {
                bankName,
                chequeNumber
            } : null
        };

        onSelectPaymentMethod(paymentRecord);
        onClose();
    };

    const isFormValid = () => {
        if (!selectedPaymentMethod || paymentAmount === null || paymentAmount < 0) return false;
        
        if (selectedPaymentMethod.code === 'CHEQUE') {
            return bankName.trim() !== '' && chequeNumber.trim() !== '';
        }
        
        return true;
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel id="payment-method-label">
                        <Translate id="hospital.billing.payment_method.select" />
                    </InputLabel>
                    <Select
                        labelId="payment-method-label"
                        value={selectedPaymentMethod?.id || ''}
                        onChange={handlePaymentMethodChange}
                        label={<Translate id="hospital.billing.payment_method.select" />}
                    >
                        {paymentMethods.map((method) => (
                            <MenuItem key={method.id} value={method.id}>
                                <ChipPaymentMethod code={method.code} label={method.customName} size="small" style={{marginRight: 8}} />
                                
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label={<Translate id="hospital.billing.payment_method.amount" />}
                    variant="outlined"
                    type="text"
                    value={paymentAmountDisplay}
                    onChange={handleAmountChange}
                    onBlur={handleAmountBlur}
                    onFocus={handleAmountFocus}
                    inputProps={{ inputMode: 'decimal' }}
                    required
                />
            </Grid>

            {selectedPaymentMethod?.code === 'CHEQUE' && (
                <>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={<Translate id="hospital.billing.payment_method.cheque_data.bank_name" />}
                            variant="outlined"
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={<Translate id="hospital.billing.payment_method.cheque_data.cheque_number" />}
                            variant="outlined"
                            value={chequeNumber}
                            onChange={(e) => setChequeNumber(e.target.value)}
                            required
                        />
                    </Grid>
                </>
            )}

            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <ButtonCancel onClick={onClose}>
                    <Translate id="general.cancel" />
                </ButtonCancel>
                <ButtonOk 
                    disabled={!isFormValid()} 
                    onClick={handleConfirm}
                >
                    <Translate id="general.confirm" />
                </ButtonOk>
            </Grid>
        </Grid>
    );
};

export default PaymentMethodModal; 